import { defineNuxtPlugin } from "#app";
import * as Sentry from "@sentry/vue";
import { withScope } from "@sentry/vue";
import type { Router } from "vue-router";
import { sentryFilterOptions } from "~/utils/sentryFilters";

// https://github.com/nuxt-community/sentry-module/issues/530
export default defineNuxtPlugin({
  parallel: true,
  setup: (nuxtApp) => {
    const config = nuxtApp.$config.public;
    const firstPartyOrigin = window.location.origin;

    Sentry.init({
      app: nuxtApp.vueApp,
      autoSessionTracking: true,
      dsn: config.sentryDsn,
      release: config.siteVersion,
      environment: config.siteEnv,
      integrations: [
        Sentry.browserTracingIntegration({
          router: nuxtApp.$router as Router,
          routeLabel: "path"
        }),
        // Sentry.replayIntegration({
        //   networkDetailAllowUrls: [
        //     `https//${nuxtApp.$config.public.HOST_NAME}`,
        //   ],
        // }),
        // Report failed HTTP responses as errors. Limited to our APIs so
        // third-party 4xx/5xx (analytics, chat, embeds) stay out of Sentry.
        // Our own origin is deliberately excluded: on the static site its only
        // fetches are prerendered payloads and POST /api/graphql, and
        // usePageQuery already reports the latter with the page key.
        // Headers and cookies are not attached (sendDefaultPii is off).
        Sentry.httpClientIntegration({
          failedRequestStatusCodes: [
            [400, 499],
            [500, 599]
          ],
          // Sentry matches string targets with `includes`, so an empty base
          // URL (e.g. a blank env var) would match every request. Drop those.
          failedRequestTargets: [config.leadApiBaseUrl, config.asuoApiBaseUrl].filter(Boolean)
        })
        // Sentry.reportingObserverIntegration(),
      ],
      // Per-component spans were ~80% of span volume and mostly <Anonymous>
      // (prod builds drop __file). To profile one component, give it a name
      // and list it here as "<Name>" with hooks: ["mount"].
      trackComponents: false,
      // Sample rates live in runtimeConfig.public (nuxt.config.ts) so each
      // environment can override them via NUXT_PUBLIC_SENTRY_* env vars.
      tracesSampleRate: config.sentryTracesSampleRate,
      sampleRate: config.sentrySampleRate,
      // ignoreTransactions: ["Vue <<Anonymous>>"],
      // Only take effect once replayIntegration above is enabled.
      replaysSessionSampleRate: config.sentryReplaysSessionSampleRate,
      replaysOnErrorSampleRate: config.sentryReplaysOnErrorSampleRate,
      // Third-party script errors are outside our control and burn plan quota.
      ...sentryFilterOptions(firstPartyOrigin, { leadApiPath: config.leadApiPath })
    });

    nuxtApp.vueApp.config.errorHandler = (err, context) => {
      withScope((scope) => {
        scope.setExtra("context", context);
        Sentry.captureException(err);
      });
    };

    nuxtApp.hook("app:error", (err) => {
      Sentry.captureException(err);
    });

    return {
      provide: {
        sentrySetContext: Sentry.setContext,
        sentrySetUser: Sentry.setUser,
        sentrySetTag: Sentry.setTag,
        sentryAddBreadcrumb: Sentry.addBreadcrumb,
        sentryCaptureException: Sentry.captureException
      }
    };
  }
});
